/**
 * Social sign-in.
 *
 * This is a real form POST, not a fetch: the provider redirects the browser away and back, and a
 * background request cannot follow that. The form carries the CSRF token as a hidden field
 * because it is a plain submission, not an XHR with a header.
 *
 * Nothing renders when no provider has credentials, so the button cannot be clicked into an
 * error before the OAuth client exists.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { providerRedirect, usableProviders } from "@/api/auth";
import { csrfToken } from "@/api/csrf";
import { authConfigQuery } from "@/features/auth/session";
import { Button } from "@/components/ui/Field";

export function SocialButtons({ next = "/" }: { next?: string }) {
  const { t } = useTranslation();
  const config = useQuery(authConfigQuery);
  const providers = usableProviders(config.data);

  if (providers.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs text-stone-500">
        <span className="h-px flex-1 bg-stone-200" />
        {t("auth.or")}
        <span className="h-px flex-1 bg-stone-200" />
      </div>
      {providers.map((provider) => {
        const { url, fields } = providerRedirect(provider.id, next);
        return (
          <form key={provider.id} method="post" action={url}>
            {Object.entries(fields).map(([name, value]) => (
              <input key={name} type="hidden" name={name} value={value} />
            ))}
            <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken() ?? ""} />
            <Button type="submit" variant="secondary">
              {t("auth.continueWith", { provider: provider.name })}
            </Button>
          </form>
        );
      })}
    </div>
  );
}
